import NumberInput from "../../NumberInput";
import { useEffect, useMemo, useRef, useState } from "react";
import { formatIDR } from "../../../utils/helpers/formatIDR";
import { isAutomaticPaymentMethod, normalizeCurrencyInput } from "../../../pages/PosPage/paymentCalculations";
import PaymentQrisPanel from "../PaymentQrisPanel";

const PAYMENT_METHODS = [
  { value: "cash", label: "Cash", description: "Tunai dari customer" },
  { value: "qris", label: "QRIS", description: "Nominal otomatis pas" },
  { value: "transfer", label: "Transfer", description: "Nominal otomatis pas" },
];

const QUICK_CASH_AMOUNTS = [50000, 100000, 150000];

const DraftPaymentModal = ({
  idModal = "process-draft-sale",
  transaction,
  loading = false,
  storeSetting,
  onConfirm,
}) => {
  const closeButtonRef = useRef(null);
  const total = Number(transaction?.total || 0);
  const storedPay = Number(transaction?.pay || 0);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [note, setNote] = useState("");
  const [validationAttempted, setValidationAttempted] = useState(false);

  useEffect(() => {
    const method = transaction?.payment_method || "cash";
    setPaymentMethod(method);
    setPaymentAmount(isAutomaticPaymentMethod(method) ? total : storedPay || total || "");
    setNote("");
    setValidationAttempted(false);
  }, [storedPay, total, transaction?.id, transaction?.payment_method]);

  const amount = Number(paymentAmount || 0);
  const due = Math.max(0, total - amount);
  const change = Math.max(0, amount - total);
  const willBePaid = total > 0 && amount >= total;
  const isInvalid = !transaction?.id || total <= 0 || paymentAmount === "" || amount < 0 || !paymentMethod;
  const productSummary = useMemo(
    () => (transaction?.details || []).map((item) => item?.product_name).filter(Boolean).join(", "),
    [transaction?.details],
  );

  const handleMethodChange = (method) => {
    setPaymentMethod(method);
    if (isAutomaticPaymentMethod(method)) {
      setPaymentAmount(total);
    }
  };

  const handleSubmit = async () => {
    setValidationAttempted(true);
    if (isInvalid || loading) return;

    const success = await onConfirm?.(transaction.id, {
      pay: amount,
      payment_method: paymentMethod,
      note,
    });

    if (success) {
      closeButtonRef.current?.click();
    }
  };

  return (
    <div
      className="modal modal-blur fade"
      id={`modal-${idModal}`}
      tabIndex="-1"
      role="dialog"
      aria-hidden="true"
      data-bs-backdrop="static"
      data-bs-keyboard="false">
      <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <div>
              <div className="text-uppercase text-primary small fw-bold mb-1">Checkout transaksi draft</div>
              <h5 className="modal-title">Lengkapi Pembayaran</h5>
            </div>
            <button
              ref={closeButtonRef}
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label="Tutup"></button>
          </div>

          <div className="modal-body">
            <div className="row g-4">
              <div className="col-lg-5">
                <div className="card bg-light border-0 h-100">
                  <div className="card-body">
                    <div className="text-secondary small fw-bold text-uppercase mb-3">Ringkasan transaksi</div>
                    <div className="d-flex justify-content-between gap-3 mb-2">
                      <span className="text-secondary">Customer</span>
                      <span className="fw-bold text-end">{transaction?.customer_name || "Umum"}</span>
                    </div>
                    <div className="d-flex justify-content-between gap-3 mb-3">
                      <span className="text-secondary">Produk</span>
                      <span className="fw-semibold text-end text-truncate" title={productSummary}>
                        {productSummary || "-"}
                      </span>
                    </div>
                    <div className="border-top pt-3 d-flex justify-content-between align-items-end gap-3">
                      <span className="text-secondary">Total transaksi</span>
                      <strong className="h3 mb-0 text-primary">{formatIDR(total)}</strong>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-lg-7">
                <div className="mb-3">
                  <label className="form-label">Metode Pembayaran <span className="akas-required-mark">*</span></label>
                  <div className="form-selectgroup form-selectgroup-boxes d-flex gap-2 draft-payment-methods">
                    {PAYMENT_METHODS.map((method) => (
                      <label className="form-selectgroup-item flex-fill" key={method.value}>
                        <input
                          type="radio"
                          name={`draft-payment-method-${transaction?.id || "sale"}`}
                          value={method.value}
                          className="form-selectgroup-input"
                          checked={paymentMethod === method.value}
                          onChange={() => handleMethodChange(method.value)}
                        />
                        <span className="form-selectgroup-label d-flex flex-column align-items-start h-100 p-3">
                          <strong>{method.label}</strong>
                          <small className="text-secondary mt-1">{method.description}</small>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Jumlah Bayar <span className="akas-required-mark">*</span></label>
                  <div className="input-group">
                    <span className="input-group-text bg-primary text-white">Rp</span>
                    <NumberInput
                      type="number"
                      min="0"
                      className={`form-control form-control-lg ${validationAttempted && (paymentAmount === "" || amount < 0) ? "is-invalid" : ""}`}
                      value={paymentAmount}
                      readOnly={isAutomaticPaymentMethod(paymentMethod)}
                      onWheel={(event) => event.currentTarget.blur()}
                      onChange={(event) => setPaymentAmount(normalizeCurrencyInput(event.target.value))}
                      placeholder="0"
                    />
                  </div>
                  {validationAttempted && paymentAmount === "" ? (
                    <div className="invalid-feedback d-block">Jumlah bayar wajib diisi.</div>
                  ) : validationAttempted && amount < 0 ? (
                    <div className="invalid-feedback d-block">Jumlah bayar tidak boleh negatif.</div>
                  ) : null}
                  {isAutomaticPaymentMethod(paymentMethod) ? (
                    <div className="form-hint mt-1">Nominal otomatis mengikuti total transaksi.</div>
                  ) : (
                    <div className="d-flex flex-wrap gap-2 mt-2">
                      <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setPaymentAmount(total)}>
                        Uang Pas
                      </button>
                      {QUICK_CASH_AMOUNTS.map((quickAmount) => (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          key={quickAmount}
                          onClick={() => setPaymentAmount(quickAmount)}>
                          {quickAmount / 1000}rb
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {paymentMethod === "qris" ? (
                  <PaymentQrisPanel amount={amount} storeSetting={storeSetting} />
                ) : null}

                <div className="row g-2 mb-3">
                  <div className="col-6">
                    <div className="rounded-3 border p-3 h-100">
                      <small className="text-secondary d-block">Sisa tagihan</small>
                      <strong className={due > 0 ? "text-danger" : "text-success"}>{formatIDR(due)}</strong>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="rounded-3 border p-3 h-100">
                      <small className="text-secondary d-block">Kembalian</small>
                      <strong>{formatIDR(change)}</strong>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="form-label">Catatan</label>
                  <textarea
                    className="form-control"
                    rows="2"
                    maxLength="500"
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                    placeholder="Opsional, contoh: pembayaran via rekening BCA"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <div className="me-auto">
              <span className={`badge ${willBePaid ? "bg-success-lt" : "bg-danger-lt"}`}>
                Setelah diproses: {willBePaid ? "Lunas" : "Belum Lunas"}
              </span>
            </div>
            <button type="button" className="btn btn-link link-secondary" data-bs-dismiss="modal" disabled={loading}>
              Batal
            </button>
            <button type="button" className="btn btn-primary" disabled={loading} onClick={handleSubmit}>
              {loading ? "Memproses..." : "Simpan Pembayaran & Proses"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DraftPaymentModal;
